import { requisitarJson } from '../../../shared/api/http';

export type EstadoDaFase = 'nao_iniciada' | 'em_andamento' | 'finalizada';

export interface FaseAtual {
    fase: string;
    rotulo: string;
    /** Minutos de jogo na estação; null no Menu Resolução, que não tem relógio de estação. */
    minutos: number | null;
    pausada: boolean;
}

export interface FaseDoCard {
    fase: string;
    rotulo: string;
    estado: EstadoDaFase;
    /** Soma do que já foi apurado; null sem apuração (`DP-RA-026`). */
    pontos: number | null;
    /** Só no Scan Pixel: milissegundos que o líder levou, do primeiro acesso ao último enigma. */
    tempo_ms?: number | null;
}

export interface CardDoGrupo {
    grupo_id: string;
    nome: string | null;
    participantes: number;
    online: number;
    finalizado: boolean;
    fase_atual: FaseAtual | null;
    fases: FaseDoCard[];
    /** O total do grupo no ranking de grupo do jogo; não é a soma das linhas de `fases` (`RN-RA-009`). */
    pontos_no_ranking: number;
}

/**
 * Como a tela chama o grupo: o nome cadastrado já pode vir como "Grupo 07" (o servidor nomeia assim o
 * grupo criado pela Operação, `DP-RA-042`); sem nome, o id.
 */
export function rotuloDoGrupo(grupo: { grupo_id: string; nome: string | null }): string {
    const nome = grupo.nome?.trim();
    if (nome && /^grupo\b/i.test(nome)) return nome;
    return `Grupo ${nome || grupo.grupo_id}`;
}

/**
 * O tempo do líder com as unidades escritas: `45,300 s`, `12 min 34,567 s`, `1 h 02 min 04,004 s`.
 * O servidor manda milissegundos e a tela só formata, sem perder a precisão (`RN-RA-009`); o que é
 * zero à esquerda não aparece, e os segundos têm 2 dígitos quando há minutos antes deles.
 */
export function formatarTempoDoLider(ms: number): string {
    const total = Math.max(0, Math.round(ms));
    const par = (valor: number, casas = 2) => String(valor).padStart(casas, '0');
    const horas = Math.floor(total / 3_600_000);
    const minutos = Math.floor(total / 60_000) % 60;
    const segundos = Math.floor(total / 1_000) % 60;
    const decimais = par(total % 1_000, 3);
    if (horas > 0) return `${horas} h ${par(minutos)} min ${par(segundos)},${decimais} s`;
    if (minutos > 0) return `${minutos} min ${par(segundos)},${decimais} s`;
    return `${segundos},${decimais} s`;
}

/** O número que o próximo grupo deve ganhar, só para o título do modal; quem decide é o servidor. */
export function proximoNumeroDeGrupo(grupos: { grupo_id: string }[]): string {
    const numeros = grupos.map((g) => /^G(\d+)$/i.exec(g.grupo_id)).filter(Boolean).map((m) => Number(m![1]));
    return String((numeros.length ? Math.max(...numeros) : 0) + 1).padStart(2, '0');
}

/**
 * A fase da estação que Reiniciar/Finalizar comandam, ou `null` quando não há comando possível:
 * grupo em espera, `FINALIZADO` e só com o Menu Resolução aberto, que não é estação (`DP-RA-040`).
 */
export function estacaoAtualDoCard(grupo: CardDoGrupo): string | null {
    if (grupo.finalizado || !grupo.fase_atual || grupo.fase_atual.fase === 'menuresolucao') return null;
    return grupo.fase_atual.fase;
}

export interface VisaoDaOperacao {
    turma_id: string;
    participantes: { online: number; total: number };
    /** `conectados` null: o servidor do jogo não respondeu (`DP-RA-006`). */
    tablets: { conectados: number | null; total: number };
    grupos: CardDoGrupo[];
}

export function obterOperacao(turmaId: string): Promise<VisaoDaOperacao> {
    return requisitarJson(`/api/v1/operacao?turma_id=${encodeURIComponent(turmaId)}`);
}

export type BonusDoLaboratorio = 'concluido' | 'liberado' | 'nao_liberado' | 'aguardando';

export interface FaseDoResumo {
    fase: string;
    rotulo: string;
    estado: 'nao_iniciada' | 'em_andamento' | 'finalizada';
    pontos: number | null;
    /** Só no Scan Pixel: milissegundos que o líder levou, do primeiro acesso ao último enigma. */
    tempo_ms?: number | null;
    /** Base e bônus só vêm separados quando a conta fecha com o total (`DP-RA-030`). */
    base: number | null;
    bonus: number | null;
    colocacao: number | null;
    /** Só no Laboratório (`DP-RA-033`). */
    bonus_lab: BonusDoLaboratorio | null;
    /** O botão da caixa: `iniciar` (nunca jogada), `reiniciar` (já jogada) ou nenhum — decide o servidor. */
    comando?: 'iniciar' | 'reiniciar' | null;
}

export interface ResumoDoGrupo {
    turma_id: string;
    grupo_id: string;
    /** Todas as fases, na ordem do card; a que não começou vem `nao_iniciada`. */
    fases: FaseDoResumo[];
}

export type AcaoDeEstacao = 'reiniciar' | 'finalizar';
/** `iniciar` é o "Iniciar" da caixa da fase: abre a janela e inicia a estação do grupo. */
export type ComandoDaFaseDoGrupo = AcaoDeEstacao | 'iniciar';

export interface EstacaoComandada {
    turma_id: string;
    grupo_id: string;
    fase: string;
    acao: ComandoDaFaseDoGrupo;
    estado: string;
}

/** Inicia, reinicia ou finaliza a fase do grupo (`RA6`); `fase` é a que a tela mostra. */
export function comandarEstacao(pedido: {
    turmaId: string;
    grupoId: string;
    fase: string;
    acao: ComandoDaFaseDoGrupo;
    csrfToken: string;
}): Promise<EstacaoComandada> {
    return requisitarJson(`/api/v1/operacao/grupos/${encodeURIComponent(pedido.grupoId)}/${pedido.acao}`, {
        method: 'POST',
        csrfToken: pedido.csrfToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ turma_id: pedido.turmaId, fase: pedido.fase }),
    });
}

export interface GrupoCriado {
    turma_id: string;
    grupo_id: string;
    nome: string;
    /** Matrículas dos que entraram no grupo novo. */
    alocados: string[];
    sessoes_revogadas: number;
    /** Matrícula do líder sorteado entre os selecionados ativos, ou null (`DP-RA-049`). */
    lider: string | null;
}

/** Novo grupo (`RA7`, `DP-RA-042`): o servidor gera o id e o nome e aloca todos numa transação. */
export function criarGrupo(pedido: { turmaId: string; participantes: string[]; csrfToken: string }): Promise<GrupoCriado> {
    return requisitarJson('/api/v1/operacao/grupos', {
        method: 'POST',
        csrfToken: pedido.csrfToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ turma_id: pedido.turmaId, participantes: pedido.participantes }),
    });
}

export interface GrupoExcluido {
    turma_id: string;
    grupo_id: string;
    membros_desvinculados: number;
}

/** Excluir grupo (`RA8`, `DP-RA-004`/`044`): só o que ainda não jogou; senão `409 grupo_ja_jogou`. */
export function excluirGrupo(pedido: { turmaId: string; grupoId: string; csrfToken: string }): Promise<GrupoExcluido> {
    return requisitarJson(
        `/api/v1/operacao/grupos/${encodeURIComponent(pedido.grupoId)}?turma_id=${encodeURIComponent(pedido.turmaId)}`,
        { method: 'DELETE', csrfToken: pedido.csrfToken },
    );
}

/** A fase aberta do grupo agora, lida do banco (`GET .../fase-atual`): o modal pergunta a cada 5 s. */
export interface FaseAtualDoGrupo {
    turma_id: string;
    grupo_id: string;
    aberta: boolean;
    fase: string | null;
    rotulo: string | null;
    minutos: number | null;
    pausada: boolean;
}

export function obterFaseAtual(turmaId: string, grupoId: string): Promise<FaseAtualDoGrupo> {
    return requisitarJson(
        `/api/v1/operacao/grupos/${encodeURIComponent(grupoId)}/fase-atual?turma_id=${encodeURIComponent(turmaId)}`,
    );
}

export function obterResumoDoGrupo(turmaId: string, grupoId: string): Promise<ResumoDoGrupo> {
    return requisitarJson(
        `/api/v1/operacao/grupos/${encodeURIComponent(grupoId)}?turma_id=${encodeURIComponent(turmaId)}`,
    );
}
